import { useSopImprovementSettings } from '@/api/rpc/hooks/agent/useSopImprovements';
import { useEffect, useRef } from 'react';
import { useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChatCircleTextIcon } from '@phosphor-icons/react';
import type { TaskResponse } from '@erp/rpc';
import { useChatThread } from '@/api/rpc/hooks/ai/useChatHistory';
import { useTaskAgentThread } from '@/api/rpc/hooks/tasks/useTaskHelpRequest';
import { invalidateTaskQueries } from '@/api/rpc/hooks/tasks/invalidateTaskQueries';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentState } from '@/context/agent-state.context';
import { useAgentModule } from '@/hooks/useAgentModule';

/**
 * Nami tools that change the task from the chat. Each lands on the timeline,
 * so every task surface on the page refetches when one succeeds.
 */
const TASK_MUTATING_TOOLS = new Set([
  'record_human_input',
  'request_human_input',
  'resolve_task',
  'return_task_to_agent',
]);

function toolResultFailed(result: unknown): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    'ok' in result &&
    result.ok === false
  );
}

/**
 * Wires the task detail page to Nami's `tasks` chat module:
 *
 * - registers the module with the task id as context, so Nami's task tools
 *   are scoped to this task;
 * - resumes the caller's persisted conversation about the task (the one Nami
 *   started while working the task in the background), so a follow-up
 *   notification lands the person mid-conversation rather than on a blank
 *   chat;
 * - honors the `?chat=open` deep link from those notifications by opening the
 *   panel once and dropping the param;
 * - refetches the task surfaces whenever Nami records an answer, asks for
 *   input, resolves, or hands the task back.
 */
export function useTaskAgentModule({
  task,
  openChat,
}: {
  task: TaskResponse;
  /** `?chat=open` was present on the URL. */
  openChat: boolean;
}): { openTaskChat: () => void } {
  const gt = useGT();
  const inputNotice = useTaskFeedbackNotice(task.id);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const agentPanel = useAgentPanel();
  const { registerToolResultListener, loadThread, currentThreadId, isLoading } =
    useAgentState();

  useEffect(() => {
    return registerToolResultListener((_toolCallId, toolName, result) => {
      if (!TASK_MUTATING_TOOLS.has(toolName)) return;
      if (toolResultFailed(result)) return;
      for (const key of [
        'task-help-request',
        'task-agent-thread',
        'task-timeline',
        'task-comments',
      ]) {
        void queryClient.invalidateQueries({ queryKey: [key, task.id] });
      }
      invalidateTaskQueries(queryClient);
    });
  }, [queryClient, registerToolResultListener, task.id]);

  useAgentModule({
    moduleKey: 'tasks',
    currentPage: `Task: ${task.title}`,
    getModuleContext: () => ({ taskId: task.id }),
    placeholder: gt(
      'Ask the assistant about this task, or answer its question…',
      {
        context: 'agent chat input placeholder on the task detail page',
      },
    ),
    inputNotice,
    headerTitle: gt('Task chat', {
      context: 'agent panel header title on the task detail page',
    }),
    headerIcon: (
      <ChatCircleTextIcon
        size={18}
        weight="duotone"
        className="text-amber-600"
      />
    ),
  });

  // Resume the persisted (task, caller) thread once per thread id. Skipped
  // while a turn is streaming so we never yank a conversation mid-reply.
  const { data: agentThread } = useTaskAgentThread(task.id);
  const threadQuery = useChatThread(agentThread?.threadId ?? undefined);
  const loadedThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    const thread = threadQuery.data;
    if (!thread || isLoading) return;
    if (loadedThreadIdRef.current === thread.id) return;
    if (currentThreadId === thread.id) {
      loadedThreadIdRef.current = thread.id;
      return;
    }
    loadedThreadIdRef.current = thread.id;
    loadThread(thread, 'tasks');
  }, [threadQuery.data, isLoading, currentThreadId, loadThread]);

  // `?chat=open`: open the panel, then strip the param so a reload or a
  // back-navigation does not pop it open again.
  const openPanel = agentPanel.open;
  const openedRef = useRef(false);
  useEffect(() => {
    if (!openChat || openedRef.current) return;
    openedRef.current = true;
    openPanel();
    void navigate({
      to: '/tasks/$taskId',
      params: { taskId: task.id },
      search: (prev) => ({ ...prev, chat: undefined }),
      replace: true,
    });
  }, [openChat, openPanel, navigate, task.id]);

  // Wrapped so it is safe as an onClick handler (`open` reads its first
  // argument as the from-modal flag).
  const openTaskChat = () => openPanel();

  return { openTaskChat };
}

function useTaskFeedbackNotice(taskId: string) {
  const gt = useGT();
  const { data: sopLearning } = useSopImprovementSettings(taskId);
  if (!sopLearning?.enabled) return undefined;
  return gt(
    'Your feedback may inform shared procedure improvements after this task closes. Your private conversation remains private.',
    { context: 'privacy notice in task chat composer' },
  );
}
